/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The one true deepMerge for the i18n layer.
 *
 * Recursively merges `overlay` onto `base`: plain objects are merged key-by-key,
 * everything else (primitives, arrays) is REPLACED wholesale by the overlay
 * value. Arrays are intentionally replaced, not concatenated or merged
 * element-wise — an admin-edited FAQ array must replace the baseline array so
 * reordering and deletions take effect.
 *
 * This used to be hand-copied into three places that all had to stay in lockstep
 * (server/dataStore.ts, src/contexts/LanguageContext.tsx, scripts/prerender.mjs).
 * It now lives here as a single plain-JS ESM module so all three import the same
 * implementation: the server compiles it (tsconfig.server allowJs), Vite bundles
 * it for the client, and the prerender node script imports the source directly.
 * Keep it dependency-free and framework-agnostic so every consumer can use it.
 *
 * @param {Record<string, any>} base
 * @param {Record<string, any>} overlay
 * @returns {Record<string, any>}
 */
export function deepMerge(base, overlay) {
  const result = { ...base };
  for (const key of Object.keys(overlay)) {
    if (
      overlay[key] &&
      typeof overlay[key] === 'object' &&
      !Array.isArray(overlay[key]) &&
      result[key] &&
      typeof result[key] === 'object' &&
      !Array.isArray(result[key])
    ) {
      result[key] = deepMerge(result[key], overlay[key]);
    } else {
      result[key] = overlay[key];
    }
  }
  return result;
}
